import { createAsyncThunk } from '@reduxjs/toolkit';
import jwtAxios from '@nxsys/services/axios';
import { endPoints } from '@nxsys/services/endpoints';
import {
  GetAnalyticsPayrollDataResponse,
  GetAnalyticsRegistrationNewResponse,
  GetAnalyticsRegistrationPendingParams,
  GetAnalyticsRegistrationPendingResponse,
  GetAnalyticsRegistrationResponse,
  GetAnalyticsInvoicingResponse,
  GetAnalyticsTimesheetResponse,
  GetAnalyticsAgencyResponse,
  GetAnalyticsPensionDocumentParams,
  GetAnalyticsPensionDocumentResponse,
  GetAnalyticsPayrollValuesResponse,
  GetAnalyticsPayrollCostParams,
  GetAnalyticsPayrollCostResponse,
  GetAnalyticsPaymentsResponse,
  GetAnalyticsPayslipsProcessedResponse,
  GetAnalyticsPayslipsProcessedOverviewParams,
  GetAnalyticsRequiredAlertsResponse,
  GetAnalyticsCompaniesNoPayrollResponse,
  GetAnalyticsCompaniesNoPayrollListParams,
} from '../types';
import { AppDispatch } from '@nxsys/store';

export const getAnalyticsPayrollData = createAsyncThunk<
  GetAnalyticsPayrollDataResponse,
  void
>('analytics/getAnalyticsPayrollData', async (_, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.get(endPoints.analytics.payroll);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const getAnalyticsPayrollValues = createAsyncThunk<
  GetAnalyticsPayrollValuesResponse,
  (GetAnalyticsPensionDocumentParams & GetAnalyticsPayrollCostParams) | void
>(
  'analytics/getAnalyticsPayrollValues',
  async (params, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.analytics.payrollValues, {
        params: params || {},
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAnalyticsRegistrationNew = createAsyncThunk<
  GetAnalyticsRegistrationNewResponse,
  void
>('analytics/getAnalyticsRegistrationNew', async (_, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.get(endPoints.analytics.registrationNew);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const getAnalyticsRegistrationPending = createAsyncThunk<
  GetAnalyticsRegistrationPendingResponse,
  GetAnalyticsRegistrationPendingParams
>(
  'analytics/getAnalyticsRegistrationPending',
  async (params, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(
        endPoints.analytics.registrationPending,
        { params },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAnalyticsRegistration = createAsyncThunk<
  GetAnalyticsRegistrationResponse,
  GetAnalyticsPayrollCostParams | void
>('analytics/getAnalyticsRegistration', async (params, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.get(endPoints.analytics.registration, {
      params: params || {},
    });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const getAnalyticsInvoicing = createAsyncThunk<
  GetAnalyticsInvoicingResponse,
  void
>('analytics/getAnalyticsInvoicing', async (_, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.get(endPoints.analytics.invoicing);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const getAnalyticsInvoicingValues = createAsyncThunk<
  GetAnalyticsInvoicingResponse,
  (GetAnalyticsPensionDocumentParams & GetAnalyticsPayrollCostParams) | void
>('analytics/getAnalyticsInvoicingValues', async (_, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.get(endPoints.analytics.invoicingValues, {
      params: _ || {},
    });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const getAnalyticsPayments = createAsyncThunk<
  GetAnalyticsPaymentsResponse,
  GetAnalyticsPayrollCostParams | void
>('analytics/getAnalyticsPayments', async (params, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.get(endPoints.analytics.payments, {
      params: params || {},
    });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const getAnalyticsTimesheet = createAsyncThunk<
  GetAnalyticsTimesheetResponse,
  void
>('analytics/getAnalyticsTimesheet', async (_, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.get(endPoints.analytics.pay);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const getAnalyticsAgency = createAsyncThunk<
  GetAnalyticsAgencyResponse,
  void
>('analytics/getAnalyticsAgency', async (_, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.get(endPoints.analytics.agency);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const getAnalyticsAgencyInvoice = createAsyncThunk<
  GetAnalyticsAgencyResponse,
  (GetAnalyticsPayrollCostParams & { selectType?: string; search?: string; sortField?: string; sortType?: string }) | void
>(
  'analytics/getAnalyticsAgencyInvoice',
  async (params, { rejectWithValue }) => {
    try {
      const requestParams = {
        selectType: 'TOP_10',
        ...(params || {}),
      };
      const response = await jwtAxios.get(endPoints.analytics.agencyInvoice, {
        params: requestParams,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAnalyticsCompaniesNoPayroll = createAsyncThunk<
  GetAnalyticsCompaniesNoPayrollResponse,
  GetAnalyticsPayrollCostParams | void
>(
  'analytics/getAnalyticsCompaniesNoPayroll',
  async (params, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(
        endPoints.analytics.companiesNoPayrollList,
        {
          params: params || {},
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAnalyticsCompaniesNoPayrollList = createAsyncThunk<
  GetAnalyticsCompaniesNoPayrollResponse,
  GetAnalyticsCompaniesNoPayrollListParams | void
>(
  'analytics/getAnalyticsCompaniesNoPayrollList',
  async (params, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(
        endPoints.analytics.companiesNoPayrollList,
        {
          params: params || {},
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAnalyticsPayrollCost = createAsyncThunk<
  GetAnalyticsPayrollCostResponse,
  GetAnalyticsPayrollCostParams | void
>('analytics/getAnalyticsPayrollCost', async (params, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.get(endPoints.analytics.payrollCost, {
      params: params || {},
    });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const getAnalyticsPensionDocument = createAsyncThunk<
  GetAnalyticsPensionDocumentResponse,
  GetAnalyticsPensionDocumentParams
>(
  'analytics/getAnalyticsPensionDocument',
  async (params, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.analytics.pensionDocument, {
        params: params || {},
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAnalyticsRequiredAlerts = createAsyncThunk<
  GetAnalyticsRequiredAlertsResponse,
  GetAnalyticsPayrollCostParams | void
>(
  'analytics/getAnalyticsRequiredAlerts',
  async (params, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.analytics.requiredAlerts, {
        params: params || {},
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAnalyticsPayslipsProcessed = createAsyncThunk<
  GetAnalyticsPayslipsProcessedResponse,
  GetAnalyticsPayslipsProcessedOverviewParams | void
>(
  'analytics/getAnalyticsPayslipsProcessed',
  async (params, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(
        endPoints.analytics.payslipsProcessedOverview,
        { params: params || {} },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAllAnalyticsData = () => async (dispatch: AppDispatch) => {
  try {
    await Promise.allSettled([
      dispatch(getAnalyticsPayrollValues()).unwrap(),
      dispatch(getAnalyticsRegistrationNew()).unwrap(),
      dispatch(getAnalyticsInvoicingValues()).unwrap(),
      dispatch(getAnalyticsTimesheet()).unwrap(),
      dispatch(getAnalyticsAgencyInvoice()).unwrap(),
      dispatch(getAnalyticsCompaniesNoPayroll()).unwrap(),
    ]);
  } catch (error) {
    console.error('Error fetching all analytics data:', error);
  }
};

export const analyticsActs = {
  getAnalyticsPayrollData,
  getAnalyticsPayrollValues,
  getAnalyticsRegistrationNew,
  getAnalyticsRegistrationPending,
  getAnalyticsRegistration,
  getAnalyticsInvoicing,
  getAnalyticsInvoicingValues,
  getAnalyticsPayments,
  getAnalyticsTimesheet,
  getAnalyticsAgency,
  getAnalyticsAgencyInvoice,
  getAnalyticsCompaniesNoPayroll,
  getAnalyticsCompaniesNoPayrollList,
  getAnalyticsPayrollCost,
  getAnalyticsPensionDocument,
  getAnalyticsRequiredAlerts,
  getAnalyticsPayslipsProcessed,
};
